import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import workPolicyApplicationApi, {
  WorkPolicyApplicationApiError
} from "../../../../api/work-policy-application-api";
import type {
  ApprovalFlexibleSchedule,
  HistoricalWorkPolicyApplicationDetail,
  WorkPolicyApplicationApi
} from "../../../../api/work-policy-application-api.types";
import { FlexibleScheduleEditor } from "../../../work-type-shared/flexible-schedule-editor/flexible-schedule-editor";
import { WorkPolicySummary } from "../../../work-type-shared/work-policy-summary";
import { Content, Label, Subscription } from "../styles";
import { SelectiveApplicationPanel } from "./selective-application-panel";
import { WorkPolicyApplicationPanel } from "./work-policy-application-panel";
import {
  SurfaceIntro,
  SurfaceState,
  TimeSummary
} from "./work-policy-application.styles";
import { WorkPolicyScheduleScrollFrame } from "./work-policy-schedule-scroll-frame";

export interface WorkTypeApprovalReadonlyDetailProps {
  approvalRequestIdx?: number;
  title: string;
  description: string;
  detail?: HistoricalWorkPolicyApplicationDetail;
  api?: WorkPolicyApplicationApi;
  onBack?: () => void;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; detail: HistoricalWorkPolicyApplicationDetail }
  | { kind: "forbidden" }
  | { kind: "stale" }
  | { kind: "error" }
  | { kind: "missing-id" };

const DetailSurface = styled.section`
  display: grid;
  width: 100%;
  min-width: 0;
  gap: 12px;

  > h2 {
    margin: 0;
    font-size: 18px;
  }
`;

const SnapshotNotice = styled.p`
  margin: 8px 0 0;
  color: ${props => props.theme.colors.darkGrey};
  line-height: 1.5;
`;

const SnapshotContent = styled.div`
  width: 100%;
  min-width: 0;
`;

const ReadonlyText = styled.p`
  margin: 0;
  width: 100%;
  min-height: 24px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const weekdayLabels = [
  "월요일",
  "화요일",
  "수요일",
  "목요일",
  "금요일",
  "토요일",
  "일요일"
];

function formatMinutes(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${remainder}분`;
  return remainder === 0 ? `${hours}시간` : `${hours}시간 ${remainder}분`;
}

function formatTime(value: string | null) {
  return value?.slice(0, 5) ?? "—";
}

function detailTitle(detail: HistoricalWorkPolicyApplicationDetail) {
  if (detail.workType === "staggered") return "시차 출퇴근";
  if (detail.workType === "selective") return "선택근무";
  return detail.flexibleMode === "DAILY_DATE"
    ? "탄력근무 날짜별"
    : "탄력근무 주간";
}

function targetMinutes(detail: HistoricalWorkPolicyApplicationDetail) {
  if (detail.workType !== "flexible") return null;
  if (detail.flexibleMode === "WEEKLY_PATTERN") {
    return Math.round(detail.weeklyBaseHours * 60);
  }
  const workdays = detail.flexibleSchedule.filter(row => row.workday).length;
  return workdays > 0 ? Math.round(detail.dailyBaseHours * 60 * workdays) : null;
}

function scheduleIdentity(row: ApprovalFlexibleSchedule) {
  return row.workDate ?? `weekday-${row.weekdayNo}`;
}

function scheduleLabel(row: ApprovalFlexibleSchedule) {
  return (
    row.workDate ??
    weekdayLabels[Math.max(0, Math.min(6, (row.weekdayNo ?? 1) - 1))]
  );
}

function SnapshotDetail({
  detail
}: {
  detail: HistoricalWorkPolicyApplicationDetail;
}) {
  const title = detailTitle(detail);
  if (detail.workType === "staggered") {
    return (
      <WorkPolicyApplicationPanel
        icon="clock"
        title={title}
        description="신청 기간 전체에 적용된 출퇴근 시간을 신청 당시 값으로 표시합니다."
        modeLabel="시차 출퇴근"
        policyName={detail.profileName}
        startDate={detail.startDate}
        endDate={detail.endDate}
      >
        <SurfaceIntro>
          <h4>선택된 출퇴근 시간</h4>
          <p>현재 허용 시간 목록을 다시 조회하지 않습니다.</p>
        </SurfaceIntro>
        <TimeSummary aria-label="신청 당시 근무 시간 요약">
          <div>
            <small>출근 → 퇴근</small>
            <strong>
              {formatTime(detail.staggered.selectedStartTime)} → {formatTime(detail.staggered.selectedEndTime)}
            </strong>
          </div>
          <div>
            <small>휴게시간</small>
            <strong>휴게시간 {formatMinutes(detail.staggered.selectedBreakMinutes)}</strong>
          </div>
          <div>
            <small>실근로시간</small>
            <strong>실근로시간 {formatMinutes(detail.staggered.selectedWorkMinutes)}</strong>
          </div>
        </TimeSummary>
      </WorkPolicyApplicationPanel>
    );
  }

  if (detail.workType === "selective") {
    return (
      <SelectiveApplicationPanel
        policyName={detail.profileName}
        startDate={detail.startDate}
        endDate={detail.endDate}
        settlementStartDay={Number(
          (
            detail.settlementPeriods[0]?.settlementStartDate ?? detail.startDate
          ).slice(-2)
        )}
        snapshot={detail}
        status="ready"
        periods={detail.settlementPeriods}
        onRetry={() => undefined}
      />
    );
  }

  const previewById = new Map(
    detail.flexibleSchedule.map(row => [scheduleIdentity(row), row] as const)
  );
  const rows = detail.flexibleSchedule.map(row => ({
    id: scheduleIdentity(row),
    label: scheduleLabel(row),
    startTime: row.startTime?.slice(0, 5) ?? "",
    workMinutes: row.plannedWorkMinutes,
    isDayOff: !row.workday,
    waiveBreakAtFourHours: row.breakWaiver,
    weekday: row.weekdayNo ?? undefined
  }));
  const daily = detail.flexibleMode === "DAILY_DATE";
  return (
    <WorkPolicyApplicationPanel
      icon={daily ? "calendar" : "week"}
      title={title}
      description={
        daily
          ? "신청 당시 날짜별 근무·휴무 일정을 표시합니다."
          : "신청 기간에 반복된 월요일부터 일요일까지의 패턴을 표시합니다."
      }
      modeLabel={daily ? "날짜별 일정" : "주간 반복"}
      policyName={detail.profileName}
      startDate={detail.startDate}
      endDate={detail.endDate}
    >
      <WorkPolicyScheduleScrollFrame
        label="읽기 전용 탄력 근무 일정"
        title="저장된 일정"
      >
        <FlexibleScheduleEditor
          mode={detail.flexibleMode}
          presentation="productivity"
          readOnly
          targetMinutes={targetMinutes(detail)}
          sectionGuide="신청 당시 저장된 일정과 자동 휴게·예상 퇴근 결과입니다."
          value={rows}
          onChange={() => undefined}
          getRowPreview={row => {
            const source = previewById.get(row.id);
            if (!source?.workday) return null;
            const start = source.startTime?.slice(0, 5) ?? "";
            const end = source.expectedEndTime?.slice(0, 5) ?? "";
            return {
              automaticBreakMinutes: source.breakMinutes,
              expectedEnd: end
                ? { time: end, crossesDay: Boolean(start && end < start) }
                : null
            };
          }}
        />
      </WorkPolicyScheduleScrollFrame>
    </WorkPolicyApplicationPanel>
  );
}

function StateMessage({
  state,
  onRetry,
  onBack
}: {
  state: Exclude<LoadState, { kind: "ready" }>;
  onRetry: () => void;
  onBack: () => void;
}) {
  if (state.kind === "loading") {
    return <SurfaceState role="status">신청 상세를 불러오는 중입니다.</SurfaceState>;
  }
  const message =
    state.kind === "forbidden"
      ? "이 문서의 조회 권한이 없습니다. 결재 목록으로 안전하게 돌아가 주세요."
      : state.kind === "stale"
      ? "신청 당시 문서와 조회 정보가 일치하지 않습니다. 문서를 다시 불러와 주세요."
      : state.kind === "missing-id"
      ? "조회할 신청 문서를 식별할 수 없습니다."
      : "신청 상세를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.";
  return (
    <SurfaceState role="alert">
      <span>{message}</span>
      <div>
        {(state.kind === "error" || state.kind === "stale") && (
          <button type="button" onClick={onRetry}>다시 시도</button>
        )}
        <button type="button" onClick={onBack}>이전 화면</button>
      </div>
    </SurfaceState>
  );
}

export function WorkTypeApprovalReadonlyDetail({
  approvalRequestIdx,
  title,
  description,
  detail: providedDetail,
  api = workPolicyApplicationApi,
  onBack
}: WorkTypeApprovalReadonlyDetailProps) {
  const [state, setState] = useState<LoadState>(() =>
    providedDetail
      ? { kind: "ready", detail: providedDetail }
      : approvalRequestIdx
      ? { kind: "loading" }
      : { kind: "missing-id" }
  );

  const loadDetail = useCallback(async () => {
    if (providedDetail) {
      setState({ kind: "ready", detail: providedDetail });
      return;
    }
    if (!approvalRequestIdx) {
      setState({ kind: "missing-id" });
      return;
    }
    setState({ kind: "loading" });
    try {
      const response = await api.detail(approvalRequestIdx);
      setState({ kind: "ready", detail: response.data });
    } catch (error) {
      if (
        error instanceof WorkPolicyApplicationApiError &&
        (error.status === 403 || error.code === "WORK_POLICY_FORBIDDEN")
      ) {
        setState({ kind: "forbidden" });
      } else if (
        error instanceof WorkPolicyApplicationApiError &&
        error.code === "WORK_POLICY_STALE"
      ) {
        setState({ kind: "stale" });
      } else {
        setState({ kind: "error" });
      }
    }
  }, [api, approvalRequestIdx, providedDetail]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const handleBack = useMemo(
    () => onBack ?? (() => window.history.back()),
    [onBack]
  );
  const loadedDetail = state.kind === "ready" ? state.detail : undefined;

  return (
    <>
      <Content style={{ gridColumn: "span 3" }}>
        <Label>근무제 유형</Label>
        <Subscription>
          {loadedDetail ? (
            <SnapshotContent>
              <WorkPolicySummary
                appearance="modern"
                variant="snapshot"
                policy={{
                  id: String(loadedDetail.workPolicyId),
                  name: loadedDetail.profileName,
                  policyKind: "APPROVAL",
                  workType: loadedDetail.workType,
                  status: "ACTIVE",
                  ruleSummary: `신청 당시 기준 · 일 ${loadedDetail.dailyBaseHours}시간 · 주 ${loadedDetail.weeklyBaseHours}시간 · 연장 한도 ${loadedDetail.weeklyOvertimeLimit}시간`
                }}
              />
              <SnapshotNotice>
                현재 정책 상태와 무관한 신청 당시 정보입니다. 최신 정책 값으로 바꾸지 않습니다.
              </SnapshotNotice>
            </SnapshotContent>
          ) : (
            <StateMessage
              state={state as Exclude<LoadState, { kind: "ready" }>}
              onRetry={() => void loadDetail()}
              onBack={handleBack}
            />
          )}
        </Subscription>
      </Content>
      <Content style={{ gridColumn: "span 3" }}>
        <Label>제목</Label>
        <Subscription><ReadonlyText>{title || "입력 없음"}</ReadonlyText></Subscription>
      </Content>
      {loadedDetail && (
        <Content style={{ gridColumn: "span 3" }}>
          <DetailSurface aria-labelledby="work-policy-readonly-detail-title">
            <h2 id="work-policy-readonly-detail-title">신청 상세</h2>
            <SnapshotDetail detail={loadedDetail} />
          </DetailSurface>
        </Content>
      )}
      <Content style={{ gridColumn: "span 3" }}>
        <Label>신청 사유/비고</Label>
        <Subscription>
          <ReadonlyText>{description || "입력 없음"}</ReadonlyText>
        </Subscription>
      </Content>
    </>
  );
}
